import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { handleRequestReset, handlePasswordReset, clearMessages, resetState } from '../../store/slices/passwordResetSlice';
import ButtonLightBlue from '../../components/buttons/ButtonLightBlue';
import banner from '../../assets/img/pexels-earano-1683802.jpg'
import Logo from '../../components/Logo'
import Input from '../../components/fields/Input'
import GuestHeader from '../../layout/GuestHeader'
import Footer from '../../layout/Footer';
import ErrorMessage from '../../components/messages/ErrorMessage';
import SuccessMessage from '../../components/messages/SuccessMessage';
import {Helmet} from "react-helmet";

const PasswordForget = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const { loading, error, errors, success, resetComplete } = useSelector(
    (state) => state.passwordReset
  );

  const [step, setStep] = useState('request'); // 'request' or 'reset'
  const [email, setEmail] = useState('');
  const [resetData, setResetData] = useState({
    token: '',
    email: '',
    password: '',
    password_confirmation: '',
  });

  // Check for token and email in URL parameters
  useEffect(() => {
    const token = searchParams.get('token');
    const emailParam = searchParams.get('email');
    
    if (token && emailParam) {
      setStep('reset');
      setResetData({
        token,
        email: emailParam,
        password: '',
        password_confirmation: '',
      });
      setEmail(emailParam);
    }
  }, [searchParams]);

  // Handle successful password reset
  useEffect(() => {
    if (resetComplete) {
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    }
  }, [resetComplete, navigate]);

  // Clear Redux state on component mount
  useEffect(() => {
    dispatch(resetState());
    return () => {
      dispatch(resetState());
    };
  }, [dispatch]);

  const handleRequestResetSubmit = async (e) => {
    e.preventDefault();
    const result = await dispatch(handleRequestReset({ email }));
    
    if (handleRequestReset.fulfilled.match(result)) {
      // navigate('/login')
      setResetData({ ...resetData, email });
    }
  };

  const handlePasswordResetSubmit = async (e) => {
    e.preventDefault();
    
    if (resetData.password !== resetData.password_confirmation) {
      // You might want to create a local error state for this
      alert('Passwords do not match');
      return;
    }

    dispatch(handlePasswordReset(resetData));
  };

  const handleResetDataChange = (e) => {
    setResetData({
      ...resetData,
      [e.target.name]: e.target.value,
    });
    dispatch(clearMessages());
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    dispatch(clearMessages());
  };

  const renderErrorMessages = () => {
    return Object.entries(errors).map(([field, messages]) => (
      <ErrorMessage key={field} message={`${messages.join(', ')}`} />
    ));
  };

  return (
    <>
      <Helmet>
          <title>Password Forget | Oreca Pro</title>
          <link rel="canonical" href={window.location.href} />
      </Helmet>
      <GuestHeader />
      <section 
        style={{backgroundImage: `url(${banner})`}} 
        className='guest-section relative min-h-[100vh] bg-center bg-cover'
      >
        <div 
          className='absolute inset-0'
          style={{
            background: 'linear-gradient(180deg,rgba(16, 28, 34, 0) 0%, rgba(16, 28, 34, 0.15) 46%, rgba(16, 28, 34, 0.84) 55%, rgba(16, 28, 34, 1) 64%)'
          }}
        />
        <div className='w-full max-w-[340px] absolute z-10 top-0 left-1/2 translate-x-[-50%] text-center px-4 mt-[120px] md:mt-[160px]'>
          <div className='scale-[1.4] my-5'>
            <Logo />
          </div>
          <h1 className='text-3xl font-semibold my-5'>{step === 'request' ? 'Reset Password' : 'Set New Password'}</h1>
          <p className='mt-5 mb-7 font-normal'>
            {step === 'request' 
              ? 'Enter your email to receive a reset link'
              : 'Enter your new password'
            }
          </p>

          {error && <ErrorMessage message={error} />}
          {renderErrorMessages()}

          {success && (
            <SuccessMessage message={success} />
          )}


          {step === 'request' ? (
            <form onSubmit={handleRequestResetSubmit}>
              <Input
                type="email"
                id="email"
                value={email}
                onChange={handleEmailChange}
                required
                placeholder="Enter your email address"
              />

              <ButtonLightBlue
                type="submit"
                disabled={loading}
                text={loading ? 'Sending...' : 'Send Reset Link'}
              />
            </form>
          ) : (
            <form onSubmit={handlePasswordResetSubmit}>
              <input
                type="hidden"
                id="token"
                name="token"
                value={resetData.token}
                onChange={handleResetDataChange}
                required
              />

              <Input
                type="password"
                id="password"
                name="password"
                value={resetData.password}
                onChange={handleResetDataChange}
                required
                minLength="8"
                placeholder="Enter your new password"
              />

              <Input
                type="password"
                id="password_confirmation"
                name="password_confirmation"
                value={resetData.password_confirmation}
                onChange={handleResetDataChange}
                required
                minLength="8"
                placeholder="Confirm your new password"
              />

              <ButtonLightBlue
                type="submit"
                disabled={loading}
                text={loading ? 'Resetting...' : 'Reset Password'}
              />
            </form>
          )}
          <p className='mt-7'>Back to <Link to="/login" className='text-custom-light-blue font-semibold'>Log in</Link></p>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default PasswordForget;
